import { Injectable, OnDestroy } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { environment } from "environments/environment";
import { Tenant, TenantUser } from "./models/tenant.model";
import { Response } from "./models/utils";
import { TenantFilter } from "./models/filters/tenant-filter.model";
import { GET_ALL_TENANTS_SUBSCRIPTION } from "./apollo/subscriptions";
import { Apollo } from "apollo-angular";
import { User } from "./models/user.model";
import { CurrentService } from "./current.service";

@Injectable({
  providedIn: "root",
})
export class TenantService implements OnDestroy {
  private subscriptions: Subscription[] = [];
  private apiUrl = `${environment.apiUrl}/api/tenants`;
  selectedTenant: BehaviorSubject<Tenant | null> = new BehaviorSubject<Tenant | null>(null);
  selectedTenantUser: BehaviorSubject<TenantUser | null> = new BehaviorSubject<TenantUser | null>(
    null
  );
  tenantSubject: BehaviorSubject<Tenant | null> = new BehaviorSubject<Tenant | null>(null);

  public tenants: BehaviorSubject<any> = new BehaviorSubject({});
  public filterSearch: BehaviorSubject<TenantFilter> = new BehaviorSubject<TenantFilter>(
    new TenantFilter()
  );
  storedUser: User;
  storedTenant: Tenant;

  constructor(
    private http: HttpClient,
    private _apollo: Apollo,
    private currentService: CurrentService
  ) {
    this.storedUser = currentService.currentUser.value;
    this.storedTenant = currentService.currentTenant.value;

    this.selectedTenant.next(this.storedTenant);

    setTimeout(() => this.subscribeToTenants(), 100);
  }

  getAllTenants(): Promise<Response<Tenant[]>> {
    return this.http.get<Response<Tenant[]>>(`${this.apiUrl}/all`).toPromise();
  }

  setCurrentTenant(tenant: Tenant): Promise<Response<User>> {
    return this.http.get<Response<User>>(`${this.apiUrl}/updateToken/${tenant.id}`).toPromise();
  }

  setCurrantTenantLocaly(tenant: Tenant): void {
    this.selectedTenant.next(tenant);
  }

  setCurrentTenantUser(tenantUser: TenantUser | null): void {
    this.selectedTenantUser.next(tenantUser);
  }

  getCurrentTenantUser(tenantId: string, userId: string): Promise<Response<TenantUser>> {
    return this.http
      .get<Response<TenantUser>>(`${this.apiUrl}/${tenantId}/currentTenantUser/${userId}`, {
        headers: this.getTenantHeaders(),
      })
      .toPromise();
  }

  getUsersByTenant(tenantId: string, filter?: any): Promise<Response<TenantUser[]>> {
    const params: any = {};
    if (filter) {
      if (filter.page) params.page = filter.page.toString();
      if (filter.size) params.size = filter.size.toString();
      if (filter.search) params.search = filter.search;
    }

    return this.http
      .get<Response<TenantUser[]>>(`${this.apiUrl}/${tenantId}/users`, {
        headers: this.getTenantHeaders(),
        params: params,
      })
      .toPromise();
  }

  getTenantHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    if (this.currentService.currentTenant) {
      headers = headers.set("X-Tenant-ID", this.currentService.currentTenant.value.id);
    }
    return headers;
  }

  getAllTenantsByFilter(): void {
    let filter = this.filterSearch.value;
    this.http
      .get<Response<Tenant[]>>(`${this.apiUrl}/all/${filter?.page}/${filter?.size}`)
      .subscribe((response) => {
        this.tenants.next(response);
      });
  }

  getAllTenantsByUser(userId: string): Promise<Response<Tenant[]>> {
    return this.http.get<Response<Tenant[]>>(`${this.apiUrl}/all/${userId}`).toPromise();
  }

  getAllTenantsUserByUserId(userId: string): Promise<Response<TenantUser[]>> {
    return this.http.get<Response<TenantUser[]>>(`${this.apiUrl}/all/${userId}/users`).toPromise();
  }

  getTenantById(id: string): Promise<Response<Tenant>> {
    return this.http.get<Response<Tenant>>(`${this.apiUrl}/${id}`).toPromise();
  }

  getTenantByIdPromise(id: string): Promise<Response<Tenant>> {
    return this.http.get<Response<Tenant>>(`${this.apiUrl}/${id}`).toPromise();
  }

  getTenantsByUserId(userId: string): Promise<Response<Tenant[]>> {
    return this.http.get<Response<Tenant[]>>(`${this.apiUrl}/all/${userId}`).toPromise();
  }

  createTenant(tenant: Tenant): Promise<Response<Tenant>> {
    let { id, ...body } = tenant;
    let requestBody = { data: body, filter: this.filterSearch.value };
    return this.http.post<Response<Tenant>>(this.apiUrl, requestBody).toPromise();
  }

  createTenantUser(tenantId: string, tenantUser: TenantUser): Promise<Response<TenantUser>> {
    let { id, ...body } = tenantUser;
    return this.http
      .post<Response<TenantUser>>(`${this.apiUrl}/${tenantId}/users`, body, {
        headers: this.getTenantHeaders(),
      })
      .toPromise();
  }

  updateTenant(tenant: Tenant): Promise<Response<Tenant>> {
    let requestBody = { data: tenant, filter: this.filterSearch.value };
    return this.http.put<Response<Tenant>>(`${this.apiUrl}/update`, requestBody).toPromise();
  }

  updateTenantUser(tenantId: string, tenantUser: TenantUser): Promise<Response<TenantUser>> {
    return this.http
      .put<Response<TenantUser>>(
        `${this.apiUrl}/${tenantId}/users/${tenantUser.userId}`,
        tenantUser,
        {
          headers: this.getTenantHeaders(),
        }
      )
      .toPromise();
  }

  deleteTenant(id: string): Promise<Response<boolean>> {
    let requestBody = { data: id, filter: this.filterSearch.value };
    return this.http.post<Response<boolean>>(`${this.apiUrl}/delete`, requestBody).toPromise();
  }

  deleteTenantUser(tenantId: string, tenantUserId: string): Promise<Response<boolean>> {
    return this.http
      .delete<Response<boolean>>(`${this.apiUrl}/${tenantId}/users/${tenantUserId}`, {
        headers: this.getTenantHeaders(),
      })
      .toPromise();
  }

  private subscribeToTenants(): void {
    let user = this.selectedTenantUser.value;
    this.subscriptions.push(
      this._apollo
        .subscribe({
          query: GET_ALL_TENANTS_SUBSCRIPTION,
          variables: {
            userId: user?.id?.toString() ?? "1",
          },
        })
        .subscribe(({ data }: any) => {
          this.tenants.next(data.allTenants);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }
}
